import React from "react";
import Header from "../Header";
import Footer from "../Footer";
import SEO from "../SEO";
import { Button } from "../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Badge } from "../ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { 
  StethoscopeIcon,
  ArrowRightIcon,
  StarIcon,
  ClockIcon,
  CheckCircleIcon,
  PhoneIcon,
  CalendarIcon,
  HeartIcon,
  ShieldIcon,
  AwardIcon,
  AlertTriangleIcon,
  LightbulbIcon,
  ActivityIcon,
  ZapIcon
} from "lucide-react";
import BookAppointmentModal from "../BookAppointmentModal";
import Breadcrumb from "../Breadcrumb";
import { openWhatsApp } from "../../lib/utils";

const KidneyStone = () => {
  const breadcrumbItems = [
    { label: "Home", href: "/" },
    { label: "Kidney Stone Treatment", href: "/kidney-stone" }
  ];

  const stats = [
    { number: "98%", label: "Success Rate", icon: ShieldIcon },
    { number: "30min", label: "Procedure Time", icon: ClockIcon },
    { number: "1-2 Days", label: "Recovery Time", icon: ActivityIcon },
    { number: "1000+", label: "Successful Cases", icon: AwardIcon }
  ];

  const causes = [
    "Insufficient water intake and dehydration",
    "High oxalate foods (spinach, nuts, chocolate)",
    "Excessive salt and protein consumption",
    "Family history of kidney stones",
    "Certain medical conditions (gout, hyperparathyroidism)",
    "Obesity and sedentary lifestyle",
    "Certain medications and supplements",
    "Hot climate and excessive sweating"
  ];

  const symptoms = [
    "Severe pain in back, side, or lower abdomen",
    "Pain that radiates to groin and genitals",
    "Nausea and vomiting",
    "Frequent urination with urgency",
    "Blood in urine (hematuria)",
    "Cloudy or foul-smelling urine",
    "Fever and chills (if infection present)",
    "Inability to find comfortable position"
  ];

  const procedures = [
    {
      name: "ESWL (Extracorporeal Shock Wave Lithotripsy)",
      description: "Non-invasive treatment using sound waves to break stones",
      duration: "30-45 minutes",
      recovery: "1-2 days",
      benefits: ["No incisions", "Outpatient procedure", "Quick recovery", "Effective for small stones"]
    },
    {
      name: "Ureteroscopy with Laser Lithotripsy",
      description: "Minimally invasive procedure using laser to break stones",
      duration: "45-60 minutes",
      recovery: "1-2 days",
      benefits: ["Precise stone removal", "High success rate", "Minimal discomfort", "Same-day discharge"]
    },
    {
      name: "PCNL (Percutaneous Nephrolithotomy)",
      description: "Surgical removal of large stones through small incision",
      duration: "1-2 hours",
      recovery: "2-3 days",
      benefits: ["Large stone removal", "Complete clearance", "Minimal scarring", "High success rate"]
    }
  ];

  const prevention = [
    "Drink 8-10 glasses of water daily",
    "Limit sodium intake to less than 2,300mg/day",
    "Reduce oxalate-rich foods in diet",
    "Maintain healthy weight and exercise regularly",
    "Limit animal protein consumption",
    "Get adequate dietary calcium from foods",
    "Avoid excessive vitamin C supplements",
    "Monitor and treat underlying conditions"
  ];

  const diagnosis = [
    "Urine analysis for crystals and blood",
    "Blood tests (calcium, uric acid, creatinine)",
    "X-ray of kidneys, ureters, and bladder (KUB)",
    "CT scan for detailed stone imaging",
    "Ultrasound of kidneys and bladder",
    "24-hour urine collection for analysis",
    "Intravenous pyelogram (IVP) if needed",
    "Stone analysis for composition"
  ];

  const kidneyStoneStructuredData = {
    "@context": "https://schema.org",
    "@type": "MedicalProcedure",
    "name": "Kidney Stone Treatment",
    "description": "Advanced kidney stone treatment using minimally invasive techniques. Expert care for kidney stone removal with modern surgical methods and comprehensive recovery support.",
    "procedureType": "Surgical Treatment",
    "bodyLocation": "Kidney and Urinary Tract",
    "preparation": "Pre-surgical consultation and preparation",
    "followup": "Post-operative care and recovery monitoring",
    "provider": {
      "@type": "MedicalOrganization",
      "name": "TheLivCure",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "Delhi",
        "addressCountry": "IN"
      }
    },
    "medicalSpecialty": "Urology",
    "areaServed": ["Delhi", "Gurgaon", "Noida", "Faridabad"]
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO
        title="Kidney Stone Treatment"
        description="Expert kidney stone treatment at TheLivCure. Advanced minimally invasive PCNL and RIRS procedures. Experienced urologists, modern facilities, 98% success rate. Book consultation today."
        keywords="kidney stone treatment, kidney stone surgery, PCNL, RIRS, kidney stone removal Delhi, urologist, kidney stone doctor, kidney stone specialist, kidney stone treatment cost"
        url="/kidney-stone"
        structuredData={kidneyStoneStructuredData}
      />
      <Header />
      
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-50 via-white to-blue-50 py-12 sm:py-16 lg:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Breadcrumb items={breadcrumbItems} />
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center mt-8">
            <div>
              <Badge className="bg-blue-100 text-blue-800 mb-4">Urology</Badge>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
                Advanced Kidney Stone Treatment
              </h1>
              <p className="text-lg sm:text-xl text-gray-600 mb-6 leading-relaxed">
                Expert kidney stone removal with cutting-edge minimally invasive techniques. 
                Get relief from kidney stone pain with our advanced procedures including ESWL, 
                laser lithotripsy, and PCNL.
              </p>
              <p className="text-base text-gray-500 mb-8 leading-relaxed">
                Kidney stones are hard deposits of minerals and salts that form inside your kidneys. 
                They can cause excruciating pain and complications if not treated properly. Our urology 
                specialists use the latest technology including shock wave lithotripsy and laser procedures 
                to break down stones with minimal discomfort and faster recovery.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <BookAppointmentModal>
                  <Button className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-3 text-lg">
                    Book Free Consultation
                    <ArrowRightIcon className="w-5 h-5 ml-2" />
                  </Button>
                </BookAppointmentModal>
                <Button 
                  variant="outline" 
                  className="border-blue-600 text-blue-600 hover:bg-blue-50 px-8 py-3 text-lg"
                  onClick={() => openWhatsApp("9311720045", "Hi, I need consultation for kidney stone treatment")}
                >
                  <PhoneIcon className="w-5 h-5 mr-2" />
                  WhatsApp Us
                </Button>
              </div>
            </div>
            
            <div className="relative">
              <div className="bg-gradient-to-br from-blue-400 to-blue-600 rounded-2xl p-8 text-white mb-8">
                <div className="grid grid-cols-2 gap-6">
                  {stats.map((stat, index) => (
                    <div key={index} className="text-center">
                      <stat.icon className="w-8 h-8 mx-auto mb-2" />
                      <div className="text-2xl sm:text-3xl font-bold">{stat.number}</div>
                      <div className="text-blue-100 text-sm">{stat.label}</div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="bg-white rounded-lg p-6 shadow-lg">
                <div className="aspect-w-16 aspect-h-9">
                  <img 
                    src="/image/kidney_stone_1.jpg" 
                    alt="Kidney stone treatment procedure" 
                    className="w-full h-48 object-cover rounded-lg"
                  />
                </div>
                <p className="text-sm text-gray-600 mt-3 text-center">
                  Advanced ESWL kidney stone treatment
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Understanding Kidney Stones Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Understanding Kidney Stones</h2>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                Kidney stones form when certain substances in your urine become highly concentrated, 
                crystallizing into hard deposits. They can develop anywhere in the urinary tract, 
                from the kidneys to the bladder.
              </p>
              <p className="text-base text-gray-500 mb-6 leading-relaxed">
                The pain from kidney stones is often described as one of the most severe pains a person can experience. 
                It typically occurs when a stone moves from the kidney into the ureter, causing obstruction and spasms.
              </p>
              <div className="space-y-4">
                <div className="flex items-start">
                  <CheckCircleIcon className="w-6 h-6 text-blue-600 mr-3 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-gray-900">Calcium Stones</h3>
                    <p className="text-gray-600">Most common type, usually calcium oxalate or calcium phosphate</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <CheckCircleIcon className="w-6 h-6 text-blue-600 mr-3 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-gray-900">Uric Acid Stones</h3>
                    <p className="text-gray-600">Form when urine is too acidic, often in people with gout</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <CheckCircleIcon className="w-6 h-6 text-blue-600 mr-3 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-gray-900">Struvite Stones</h3>
                    <p className="text-gray-600">Form in response to urinary tract infections</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-gray-50 rounded-2xl p-8">
              <img 
                src="/image/kidney_stone_2.jpg" 
                alt="Kidney anatomy and stone formation" 
                className="w-full h-64 object-cover rounded-lg"
              />
              <p className="text-sm text-gray-600 mt-3 text-center">
                Kidney anatomy and stone formation process
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Pain and Symptoms Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Recognizing Kidney Stone Symptoms</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Kidney stone symptoms can be sudden and severe. Early recognition can help prevent complications 
              and guide appropriate treatment decisions.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <Card className="text-center p-6 hover:shadow-lg transition-shadow">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <HeartIcon className="w-8 h-8 text-red-600" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Severe Pain</h3>
              <p className="text-sm text-gray-600">Intense pain in back, side, lower abdomen, or groin that comes in waves</p>
            </Card>
            
            <Card className="text-center p-6 hover:shadow-lg transition-shadow">
              <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <ActivityIcon className="w-8 h-8 text-orange-600" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Blood in Urine</h3>
              <p className="text-sm text-gray-600">Pink, red, or brown urine due to stone irritation</p>
            </Card>
            
            <Card className="text-center p-6 hover:shadow-lg transition-shadow">
              <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <AlertTriangleIcon className="w-8 h-8 text-yellow-600" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Nausea & Vomiting</h3>
              <p className="text-sm text-gray-600">Severe nausea and vomiting due to intense pain</p>
            </Card>
            
            <Card className="text-center p-6 hover:shadow-lg transition-shadow">
              <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <ClockIcon className="w-8 h-8 text-purple-600" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Frequent Urination</h3>
              <p className="text-sm text-gray-600">Urgent need to urinate more often than usual</p>
            </Card>
          </div>
        </div>
      </section>

      {/* Advanced Treatment Technology Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Advanced Treatment Technology</h2>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                Our urology department is equipped with the latest technology for kidney stone treatment, 
                ensuring minimal discomfort and optimal outcomes for our patients.
              </p>
              
              <div className="space-y-6">
                <div className="flex items-start">
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                    <ZapIcon className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2">ESWL Technology</h3>
                    <p className="text-gray-600">Non-invasive shock wave lithotripsy breaks stones without incisions</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                    <ShieldIcon className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2">Laser Lithotripsy</h3>
                    <p className="text-gray-600">Precise laser technology for targeted stone fragmentation</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                    <AwardIcon className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2">PCNL Excellence</h3>
                    <p className="text-gray-600">Advanced percutaneous procedures for large stone removal</p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="bg-gray-50 rounded-2xl p-8">
              <img 
                src="/image/kidney_stone_3.jpg" 
                alt="Advanced medical equipment" 
                className="w-full h-64 object-cover rounded-lg"
              />
              <p className="text-sm text-gray-600 mt-3 text-center">
                State-of-the-art kidney stone treatment equipment
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Treatment Details */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Tabs defaultValue="overview" className="w-full">
            <TabsList className="grid w-full grid-cols-2 lg:grid-cols-6 mb-8">
              <TabsTrigger value="overview">Overview</TabsTrigger>
              <TabsTrigger value="causes">Causes</TabsTrigger>
              <TabsTrigger value="symptoms">Symptoms</TabsTrigger>
              <TabsTrigger value="diagnosis">Diagnosis</TabsTrigger>
              <TabsTrigger value="procedures">Procedures</TabsTrigger>
              <TabsTrigger value="prevention">Prevention</TabsTrigger>
            </TabsList>

            <TabsContent value="overview" className="space-y-8">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center">
                      <StethoscopeIcon className="w-6 h-6 mr-2 text-blue-600" />
                      Treatment Overview
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-600 mb-6">
                      Kidney stones are hard deposits of minerals and salts that form in the kidneys. 
                      Our advanced treatment options include non-invasive ESWL, minimally invasive 
                      laser lithotripsy, and surgical PCNL for complete stone removal.
                    </p>
                    <p className="text-gray-600 mb-6">
                      The kidneys filter waste from the blood and produce urine. When certain substances 
                      become highly concentrated, they can crystallize and form stones. Our urology 
                      specialists use cutting-edge technology to break down and remove these stones 
                      with minimal discomfort and excellent outcomes.
                    </p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center">
                      <AwardIcon className="w-6 h-6 mr-2 text-blue-600" />
                      Why Choose Our Treatment
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div className="flex items-start">
                        <CheckCircleIcon className="w-5 h-5 text-blue-600 mr-3 mt-1 flex-shrink-0" />
                        <div>
                          <h4 className="font-semibold text-gray-900">Expert Urologists</h4>
                          <p className="text-sm text-gray-600">Board-certified specialists with extensive stone treatment experience</p>
                        </div>
                      </div>
                      <div className="flex items-start">
                        <CheckCircleIcon className="w-5 h-5 text-blue-600 mr-3 mt-1 flex-shrink-0" />
                        <div>
                          <h4 className="font-semibold text-gray-900">Advanced Technology</h4>
                          <p className="text-sm text-gray-600">Latest ESWL, laser, and PCNL equipment for optimal results</p>
                        </div>
                      </div>
                      <div className="flex items-start">
                        <CheckCircleIcon className="w-5 h-5 text-blue-600 mr-3 mt-1 flex-shrink-0" />
                        <div>
                          <h4 className="font-semibold text-gray-900">Comprehensive Care</h4>
                          <p className="text-sm text-gray-600">Complete pre and post-procedure support and follow-up</p>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                <div className="text-center p-6 bg-blue-50 rounded-lg">
                  <ClockIcon className="w-8 h-8 text-blue-600 mx-auto mb-2" />
                  <h3 className="font-semibold mb-2">Quick Recovery</h3>
                  <p className="text-sm text-gray-600">Most procedures allow return to normal activities within 1-2 days</p>
                </div>
                <div className="text-center p-6 bg-blue-50 rounded-lg">
                  <ShieldIcon className="w-8 h-8 text-blue-600 mx-auto mb-2" />
                  <h3 className="font-semibold mb-2">High Success Rate</h3>
                  <p className="text-sm text-gray-600">98% success rate with our advanced techniques</p>
                </div>
                <div className="text-center p-6 bg-blue-50 rounded-lg sm:col-span-2 lg:col-span-1">
                  <ZapIcon className="w-8 h-8 text-blue-600 mx-auto mb-2" />
                  <h3 className="font-semibold mb-2">Minimally Invasive</h3>
                  <p className="text-sm text-gray-600">Advanced laser and shock wave technology</p>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="causes" className="space-y-8">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center">
                      <AlertTriangleIcon className="w-6 h-6 mr-2 text-orange-600" />
                      Primary Risk Factors
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {causes.slice(0, 4).map((cause, index) => (
                        <div key={index} className="flex items-start">
                          <CheckCircleIcon className="w-5 h-5 text-blue-600 mr-3 mt-0.5 flex-shrink-0" />
                          <span className="text-gray-700">{cause}</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center">
                      <HeartIcon className="w-6 h-6 mr-2 text-red-600" />
                      Additional Risk Factors
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {causes.slice(4).map((cause, index) => (
                        <div key={index} className="flex items-start">
                          <CheckCircleIcon className="w-5 h-5 text-red-600 mr-3 mt-0.5 flex-shrink-0" />
                          <span className="text-gray-700">{cause}</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </div>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <LightbulbIcon className="w-6 h-6 mr-2 text-yellow-600" />
                    Understanding Stone Formation
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    <div className="text-center p-4 bg-blue-50 rounded-lg">
                      <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
                        <span className="text-blue-600 font-bold text-lg">1</span>
                      </div>
                      <h4 className="font-semibold text-gray-900 mb-2">Urine Concentration</h4>
                      <p className="text-sm text-gray-600">High concentration of minerals leads to crystallization</p>
                    </div>
                    <div className="text-center p-4 bg-blue-50 rounded-lg">
                      <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
                        <span className="text-blue-600 font-bold text-lg">2</span>
                      </div>
                      <h4 className="font-semibold text-gray-900 mb-2">Crystal Growth</h4>
                      <p className="text-sm text-gray-600">Crystals grow larger and form stones over time</p>
                    </div>
                    <div className="text-center p-4 bg-blue-50 rounded-lg">
                      <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
                        <span className="text-blue-600 font-bold text-lg">3</span>
                      </div>
                      <h4 className="font-semibold text-gray-900 mb-2">Stone Movement</h4>
                      <p className="text-sm text-gray-600">Stones move through urinary tract causing pain</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="symptoms" className="space-y-8">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <HeartIcon className="w-6 h-6 mr-2 text-red-600" />
                    Symptoms to Watch For
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {symptoms.map((symptom, index) => (
                      <div key={index} className="flex items-start">
                        <CheckCircleIcon className="w-5 h-5 text-red-600 mr-3 mt-0.5 flex-shrink-0" />
                        <span className="text-gray-700">{symptom}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="diagnosis" className="space-y-8">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <ActivityIcon className="w-6 h-6 mr-2 text-blue-600" />
                    Diagnostic Methods
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {diagnosis.map((test, index) => (
                      <div key={index} className="flex items-start">
                        <CheckCircleIcon className="w-5 h-5 text-blue-600 mr-3 mt-0.5 flex-shrink-0" />
                        <span className="text-gray-700">{test}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="procedures" className="space-y-8">
              <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
                {procedures.map((procedure, index) => (
                  <Card key={index} className="hover:shadow-lg transition-shadow">
                    <CardHeader>
                      <CardTitle className="text-lg">{procedure.name}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-gray-600 mb-4">{procedure.description}</p>
                      
                      <div className="space-y-2 mb-4">
                        <div className="flex justify-between">
                          <span className="text-sm text-gray-500">Duration:</span>
                          <span className="text-sm font-medium">{procedure.duration}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-sm text-gray-500">Recovery:</span>
                          <span className="text-sm font-medium">{procedure.recovery}</span>
                        </div>
                      </div>
                      
                      <div>
                        <h4 className="font-medium mb-2">Benefits:</h4>
                        <ul className="space-y-1">
                          {procedure.benefits.map((benefit, idx) => (
                            <li key={idx} className="flex items-start text-sm">
                              <CheckCircleIcon className="w-4 h-4 text-blue-600 mr-2 mt-0.5 flex-shrink-0" />
                              {benefit}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <ShieldIcon className="w-6 h-6 mr-2 text-blue-600" />
                    Treatment Process Overview
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    <div className="text-center p-4 bg-blue-50 rounded-lg">
                      <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
                        <span className="text-blue-600 font-bold text-lg">1</span>
                      </div>
                      <h4 className="font-semibold text-gray-900 mb-2">Evaluation</h4>
                      <p className="text-sm text-gray-600">Complete assessment and stone analysis</p>
                    </div>
                    <div className="text-center p-4 bg-blue-50 rounded-lg">
                      <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
                        <span className="text-blue-600 font-bold text-lg">2</span>
                      </div>
                      <h4 className="font-semibold text-gray-900 mb-2">Treatment</h4>
                      <p className="text-sm text-gray-600">Advanced procedure selection and execution</p>
                    </div>
                    <div className="text-center p-4 bg-blue-50 rounded-lg">
                      <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
                        <span className="text-blue-600 font-bold text-lg">3</span>
                      </div>
                      <h4 className="font-semibold text-gray-900 mb-2">Recovery</h4>
                      <p className="text-sm text-gray-600">Post-procedure care and monitoring</p>
                    </div>
                    <div className="text-center p-4 bg-blue-50 rounded-lg">
                      <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
                        <span className="text-blue-600 font-bold text-lg">4</span>
                      </div>
                      <h4 className="font-semibold text-gray-900 mb-2">Prevention</h4>
                      <p className="text-sm text-gray-600">Long-term prevention strategies</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="prevention" className="space-y-8">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <LightbulbIcon className="w-6 h-6 mr-2 text-yellow-600" />
                    Prevention Tips
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {prevention.map((tip, index) => (
                      <div key={index} className="flex items-start">
                        <CheckCircleIcon className="w-5 h-5 text-yellow-600 mr-3 mt-0.5 flex-shrink-0" />
                        <span className="text-gray-700">{tip}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </section>

      {/* Image Gallery Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Kidney Stone Treatment Gallery</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              See our advanced kidney stone treatment facilities and procedures in action. 
              Our modern equipment and expert care ensure the best outcomes for our patients.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-white rounded-lg overflow-hidden shadow-lg hover:shadow-xl transition-shadow">
              <img 
                src="/image/kidney_stone_1.jpg" 
                alt="Kidney stone treatment procedure overview" 
                className="w-full h-48 object-cover"
              />
              <div className="p-4">
                <h3 className="font-semibold text-gray-900 mb-2">Treatment Overview</h3>
                <p className="text-sm text-gray-600">Advanced minimally invasive kidney stone removal procedures</p>
              </div>
            </div>
            
            <div className="bg-white rounded-lg overflow-hidden shadow-lg hover:shadow-xl transition-shadow">
              <img 
                src="/image/kidney_stone_2.jpg" 
                alt="Kidney anatomy and diagnosis" 
                className="w-full h-48 object-cover"
              />
              <div className="p-4">
                <h3 className="font-semibold text-gray-900 mb-2">Anatomy & Diagnosis</h3>
                <p className="text-sm text-gray-600">Understanding kidney anatomy and stone formation</p>
              </div>
            </div>
            
            <div className="bg-white rounded-lg overflow-hidden shadow-lg hover:shadow-xl transition-shadow">
              <img 
                src="/image/kidney_stone_3.jpg" 
                alt="Modern surgical equipment" 
                className="w-full h-48 object-cover"
              />
              <div className="p-4">
                <h3 className="font-semibold text-gray-900 mb-2">Modern Equipment</h3>
                <p className="text-sm text-gray-600">State-of-the-art kidney stone treatment equipment</p>
              </div>
            </div>
            
            <div className="bg-white rounded-lg overflow-hidden shadow-lg hover:shadow-xl transition-shadow">
              <img 
                src="/image/kidney_stone_4.jpg" 
                alt="Recovery and aftercare" 
                className="w-full h-48 object-cover"
              />
              <div className="p-4">
                <h3 className="font-semibold text-gray-900 mb-2">Recovery & Aftercare</h3>
                <p className="text-sm text-gray-600">Comprehensive recovery support and aftercare services</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-blue-600 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            Ready to Get Relief from Kidney Stone Pain?
          </h2>
          <p className="text-xl text-blue-100 mb-8">
            Book your consultation today and take the first step towards a pain-free life.
          </p>
          <BookAppointmentModal>
            <Button className="bg-white text-blue-600 hover:bg-gray-100 px-8 py-3 text-lg">
              Book Free Consultation
              <ArrowRightIcon className="w-5 h-5 ml-2" />
            </Button>
          </BookAppointmentModal>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default KidneyStone;
